import React, { useEffect, useRef } from 'react';
import type HlsType from 'hls.js';
import { noteReel, noteReelKick } from '../reelTelemetry';

interface MuxVideoProps {
  playbackId: string;
  className?: string;
  /** Expose the underlying <video> element (for audio control etc.) */
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  /** Held stopped, and the reason does not matter here: the visitor dismissed
   *  the player, or paused it from the transport. Kept as a prop rather than a
   *  pause() from outside so the internal retry handlers respect it — they fire
   *  on canplay and on visibilitychange, and would otherwise undo the pause the
   *  moment the stream buffered. That is not hypothetical: it is what made the
   *  transport's pause button appear to do nothing. */
  paused?: boolean;
  /** Same-origin poster. image.mux.com sets _mux_key and three Heap (_hp2_*)
   *  third-party cookies, so the hero serves its own copy of the frame. */
  poster?: string;
}

/**
 * Native HLS player for Mux streams. Uses the browser's built-in HLS on
 * Safari/iOS and hls.js everywhere else. Autoplays muted, loops, and paints
 * a Mux-generated poster frame instantly while the stream spins up.
 */
const MuxVideo: React.FC<MuxVideoProps> = ({ playbackId, className = '', videoRef: externalRef, poster, paused = false }) => {
  const internalRef = useRef<HTMLVideoElement>(null);
  const videoElRef = externalRef ?? internalRef;
  // read inside the play handlers without making them a dependency: re-running
  // the effect below would tear down and rebuild hls on every toggle
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  useEffect(() => {
    const video = videoElRef.current;
    if (!video || !playbackId) return;

    // Mux publishes 1080p/720p/480p/270p and HLS always begins on the lowest
    // rung, which is why the first seconds looked pixelated. Flooring the
    // ladder at 720p removes the two renditions responsible.
    //
    // The ceiling matters just as much, and for a long time there wasn't one:
    // capLevelToPlayerSize is off (see below), so with only a floor, ABR
    // climbed to the 1080p rung and stayed there. Mux advertises that rung at
    // 3.08 Mbps against 1.59 Mbps for 720p, which is how Lighthouse came to
    // measure 17MB of an 18MB page as reel — 98% of the transfer, for a frame
    // 392 CSS px wide on a 412px phone. Below 768px the reel is never wider
    // than about 430 CSS px, theatre mode included, so 720p is the top of what
    // the screen can resolve and the other half was being thrown away.
    //
    // Chosen once, at mount: the element lives for the whole page and is never
    // remounted, so a phone that later becomes a wide window keeps 720p. That
    // is the right way round — a desktop that narrows does not lose quality.
    //
    // Fails open on a width of 0. A hidden, prerendering or backgrounded tab
    // can report one, and `0 < 768` would quietly pin every such visitor to
    // 720p — the same trap as the pre-layout measurement that made
    // capLevelToPlayerSize unusable below.
    const vw = window.innerWidth;
    const capped = vw > 0 && vw < 768;
    const src =
      `https://stream.mux.com/${playbackId}.m3u8?min_resolution=720p` +
      (capped ? '&max_resolution=720p' : '');
    let hls: HlsType | null = null;
    let cancelled = false;

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari / iOS play HLS natively, so hls.js is never downloaded there
      video.src = src;
    } else {
      // ~180kB gzipped: fetch it only when the browser actually needs it
      import('hls.js').then(({ default: Hls }) => {
        if (cancelled || !Hls.isSupported()) return;
        hls = new Hls({
          maxBufferLength: 30,
          // capping to player size let a pre-layout measurement pin quality low
          capLevelToPlayerSize: false,
          // assume a decent pipe up front rather than ramping from the floor
          abrEwmaDefaultEstimate: 2_000_000,
        });
        hls.loadSource(src);
        hls.attachMedia(video);
      });
    }

    // The `video.paused` guard is deliberate, and was removed for a day and put
    // back. iOS grants a muted, inline element exactly one autoplay allowance,
    // and spends it on the first play() it sees. Calling play() again on an
    // element that is already playing is a no-op on paper, but on WebKit it is
    // a fresh request against an allowance that is gone, and a refused request
    // is what makes iOS paint its own start-playback button over the reel.
    //
    // Reported from a real iPhone with that button sitting on the hero. Whatever
    // the exact mechanism, this shape autoplayed on iOS for months and the
    // unguarded one did not, which settles it.
    const tryPlay = () => {
      if (pausedRef.current) return;
      if (video.paused) {
        video.play().catch(() => {
          /* Autoplay blocked (e.g. hidden tab) — retried on canplay/visibility */
        });
      }
    };
    const onVisible = () => {
      if (!document.hidden) tryPlay();
    };
    video.addEventListener('loadedmetadata', tryPlay);
    video.addEventListener('canplay', tryPlay);
    document.addEventListener('visibilitychange', onVisible);
    tryPlay();

    return () => {
      cancelled = true;
      video.removeEventListener('loadedmetadata', tryPlay);
      video.removeEventListener('canplay', tryPlay);
      document.removeEventListener('visibilitychange', onVisible);
      if (hls) hls.destroy();
      video.removeAttribute('src');
      video.load();
    };
  }, [playbackId, videoElRef]);

  /**
   * A stalled reel is not a paused reel, and that distinction cost a day.
   *
   * Chrome will accept play() on a video that is not being rendered, report
   * `paused: false` and reach readyState 4, and never advance the clock — and
   * it does not resume when the element becomes visible again. iOS has its own
   * routes into the same state. The visible symptom is a still frame, which is
   * indistinguishable from the poster, so it reads as "the reel didn't load".
   *
   * Every retry above is guarded by `if (video.paused)`, which is exactly false
   * here, so none of them fire. Nothing recovers it on its own.
   *
   * This used to call play() to restart it. It no longer does. On iOS that is
   * an unprompted request against a spent autoplay allowance, and a refused
   * request is worse than a stalled one: it puts WebKit's start-playback button
   * on the hero. A rescue that can break the common case to fix the rare one is
   * not a rescue.
   *
   * So it only watches now, and what it sees goes out with the visit. If the
   * dashboard shows never_started with readyState 4, the stall is real and can
   * be fixed against evidence rather than against a guess.
   */
  useEffect(() => {
    const video = videoElRef.current;
    if (!video) return;
    let seen = -1;
    const id = window.setInterval(() => {
      // The tick is already here, so it is also where the visit's account of
      // the reel is kept. See reelTelemetry.ts for why any of this exists.
      noteReel(video.readyState, video.currentTime, video.duration);
      if (pausedRef.current || video.paused || video.readyState < 3) {
        seen = -1;
        return;
      }
      if (seen >= 0 && video.currentTime === seen) noteReelKick();
      seen = video.currentTime;
    }, 1000);
    return () => window.clearInterval(id);
  }, [videoElRef]);

  // Stopping is the point of the dismiss control: a hidden reel that keeps
  // streaming is still ~1.59 Mbps of someone's connection.
  useEffect(() => {
    const video = videoElRef.current;
    if (!video) return;
    if (paused) video.pause();
    else void video.play().catch(() => {});
  }, [paused, videoElRef]);

  return (
    <video
      ref={videoElRef}
      className={className}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      crossOrigin="anonymous"
      poster={poster ?? `https://image.mux.com/${playbackId}/thumbnail.webp?time=1&width=1920`}
    />
  );
};

export default MuxVideo;
